import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useApiClient } from '@/hooks/useApiClient';
import { useToast } from '@/contexts/ToastContext';

const XIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const CheckIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
  </svg>
);

const ImageIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
  </svg>
);

const CameraIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);

const TrashIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
  </svg>
);

const ChevronLeftIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
  </svg>
);

const CLASSIFICATION_OPTIONS = ['matin', 'midi', 'soir'] as const;
type Classification = typeof CLASSIFICATION_OPTIONS[number];

export function CreateProductForm({
  onClose,
  onSuccess,
}: {
  onClose: () => void;
  onSuccess: () => void;
}) {
  const { t } = useTranslation();
  const apiClient = useApiClient();
  const { addToast } = useToast();
  const queryClient = useQueryClient();

  const [step, setStep] = useState<1 | 2>(1);
  const [classification, setClassification] = useState<Classification | ''>('');

  const [formData, setFormData] = useState({
    name: '',
    supplier: '',
    batchNumber: '',
    expiryDate: '',
    quantity: '',
    quantityUnit: 'kg',
    storageLocation: '',
  });

  const [photos, setPhotos] = useState<File[]>([]);
  const [photoPreview, setPhotoPreview] = useState<string[]>([]);

  const createProductMutation = useMutation({
    mutationFn: async () => {
      const product = await apiClient.createProduct({
        name: formData.name,
        ...(formData.supplier && { supplier: formData.supplier }),
        ...(formData.batchNumber && { batchNumber: formData.batchNumber }),
        ...(formData.expiryDate && { expiryDate: formData.expiryDate }),
        ...(formData.quantity && { quantity: parseInt(formData.quantity, 10) }),
        ...(formData.quantityUnit && { quantityUnit: formData.quantityUnit }),
        ...(formData.storageLocation && { storageLocation: formData.storageLocation }),
        ...(classification && { classification }),
      });

      for (const photo of photos) {
        const photoFormData = new FormData();
        photoFormData.append('file', photo);
        await apiClient.addProductPhoto(product.id, photoFormData);
      }

      return product;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      addToast(t('traceability.addSuccess'), 'success');
      onSuccess();
    },
    onError: () => {
      addToast(t('traceability.addError'), 'error');
    },
  });

  const handlePhotoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      const newPhotos = [...photos, ...files];
      setPhotos(newPhotos);

      files.forEach((file) => {
        const reader = new FileReader();
        reader.onload = (event) => {
          setPhotoPreview((prev) => [...prev, event.target?.result as string]);
        };
        reader.readAsDataURL(file);
      });
    }
  };

  const removePhoto = (index: number) => {
    setPhotos((prev) => prev.filter((_, i) => i !== index));
    setPhotoPreview((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      addToast(t('traceability.nameRequired'), 'error');
      return;
    }
    if (photos.length === 0) {
      addToast(t('traceability.photoRequired'), 'error');
      return;
    }
    createProductMutation.mutate();
  };

  const selectClassification = (value: Classification) => {
    setClassification(value);
    setStep(2);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex flex-col items-center justify-start sm:justify-center overflow-y-auto p-4">
      <div className="bg-white rounded-lg shadow-2xl max-w-md w-full my-4 max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 flex items-center justify-between p-6 border-b border-slate-200 bg-white">
          <div className="flex items-center gap-3">
            {step === 2 && (
              <button
                type="button"
                onClick={() => setStep(1)}
                className="p-1 hover:bg-slate-100 rounded-lg transition"
              >
                <ChevronLeftIcon className="w-5 h-5 text-slate-600" />
              </button>
            )}
            <div>
              <h2 className="text-xl font-bold text-slate-900">{t('traceability.formTitle')}</h2>
              <p className="text-xs text-slate-400 mt-0.5">
                {t('traceability.stepIndicator', { current: step, total: 2 })}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-slate-100 rounded-lg transition"
          >
            <XIcon className="w-6 h-6 text-slate-600" />
          </button>
        </div>

        {step === 1 && (
          <div className="p-6">
            <p className="text-base font-semibold text-slate-800 mb-4">
              {t('traceability.classificationLabel')}
            </p>
            <div className="divide-y divide-slate-100 border border-slate-200 rounded-lg overflow-hidden mb-4">
              {CLASSIFICATION_OPTIONS.map((option) => (
                <button
                  key={option}
                  type="button"
                  onClick={() => selectClassification(option)}
                  className="w-full flex items-center justify-between px-4 py-4 text-left text-slate-800 font-medium hover:bg-slate-50 active:bg-slate-100 transition"
                >
                  <span className="capitalize">{t(`traceability.classification_${option}`)}</span>
                  <ChevronLeftIcon className="w-4 h-4 text-slate-400 rotate-180" />
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={() => { setClassification(''); setStep(2); }}
              className="w-full py-2 text-sm text-slate-500 hover:text-slate-700 transition"
            >
              {t('traceability.classificationSkip')}
            </button>
          </div>
        )}

        {step === 2 && (
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            {classification && (
              <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg">
                <span className="text-xs font-medium text-blue-600 uppercase tracking-wide">
                  {t('traceability.classification')}
                </span>
                <span className="text-sm font-semibold text-blue-800 capitalize">
                  {t(`traceability.classification_${classification}`)}
                </span>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                {t('traceability.productName')}
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder={t('traceability.productNamePlaceholder')}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                {t('traceability.photosLabel')}
              </label>
              <div className="mb-3 grid grid-cols-2 gap-2">
                <label className="flex flex-col items-center justify-center px-3 py-5 border-2 border-dashed border-blue-400 bg-blue-50 rounded-lg cursor-pointer hover:bg-blue-100 transition">
                  <CameraIcon className="w-7 h-7 text-blue-600 mb-1" />
                  <span className="text-sm font-medium text-blue-700 text-center">{t('traceability.takePhoto')}</span>
                  <input
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={handlePhotoSelect}
                    className="hidden"
                  />
                </label>
                <label className="flex flex-col items-center justify-center px-3 py-5 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:border-slate-400 hover:bg-slate-50 transition">
                  <ImageIcon className="w-7 h-7 text-slate-400 mb-1" />
                  <span className="text-sm text-slate-600 text-center">{t('traceability.uploadPhotos')}</span>
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={handlePhotoSelect}
                    className="hidden"
                  />
                </label>
              </div>

              {photoPreview.length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {photoPreview.map((preview, idx) => (
                    <div key={idx} className="relative group">
                      <img
                        src={preview}
                        alt={`Preview ${idx}`}
                        className="w-full h-20 object-cover rounded-lg"
                      />
                      <button
                        type="button"
                        onClick={() => removePhoto(idx)}
                        className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 group-hover:opacity-100 transition rounded-lg"
                      >
                        <TrashIcon className="w-5 h-5 text-white" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                {t('traceability.supplierOptional')}
              </label>
              <input
                type="text"
                value={formData.supplier}
                onChange={(e) => setFormData({ ...formData, supplier: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="ex. ABC Farms"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                {t('traceability.batchOptional')}
              </label>
              <input
                type="text"
                value={formData.batchNumber}
                onChange={(e) => setFormData({ ...formData, batchNumber: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder={t('traceability.batchPlaceholder')}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                {t('traceability.expiryOptional')}
              </label>
              <input
                type="date"
                value={formData.expiryDate}
                onChange={(e) => setFormData({ ...formData, expiryDate: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  {t('traceability.quantityOptional')}
                </label>
                <input
                  type="number"
                  value={formData.quantity}
                  onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="0"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  {t('traceability.unit')}
                </label>
                <select
                  value={formData.quantityUnit}
                  onChange={(e) => setFormData({ ...formData, quantityUnit: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="kg">kg</option>
                  <option value="L">L</option>
                  <option value="pieces">{t('traceability.pieces')}</option>
                  <option value="boxes">{t('traceability.boxes')}</option>
                  <option value="g">g</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                {t('traceability.storageOptional')}
              </label>
              <input
                type="text"
                value={formData.storageLocation}
                onChange={(e) => setFormData({ ...formData, storageLocation: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder={t('traceability.storagePlaceholder')}
              />
            </div>

            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition"
              >
                {t('traceability.cancel')}
              </button>
              <button
                type="submit"
                disabled={createProductMutation.isPending}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <CheckIcon className="w-5 h-5" />
                {createProductMutation.isPending ? t('traceability.creating') : t('traceability.create')}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
